import React from 'react';
import Styles from '../BrandInsightsDashboard.module.scss';
import { FiRefreshCcw, FiInstagram, FiLinkedin, FiTwitter, FiYoutube, FiArrowRight } from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';

const PLATFORM_ICONS = {
  instagram: FiInstagram,
  linkedin: FiLinkedin,
  twitter: FiTwitter,
  youtube: FiYoutube,
  tiktok: FaTiktok,
};

const PLATFORM_COLORS = {
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2',
  youtube: '#FF0000',
  tiktok: '#000000',
};

const PLATFORM_NAMES = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'Twitter/X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

const CrossPlatformInsights = ({ crossPlatform, platformsAnalyzed }) => {
  // Only show if multiple platforms
  if (!crossPlatform || (platformsAnalyzed && platformsAnalyzed.length < 2)) {
    return null;
  }

  const {
    consistency_summary = '',
    platform_adaptations = {},
    shared_voice_elements = [],
    unique_per_platform = {},
    content_translation_opportunities = [],
    strengths_per_platform = {}
  } = crossPlatform;

  // Check if we have meaningful data
  const hasData = consistency_summary ||
    shared_voice_elements.length > 0 ||
    Object.keys(platform_adaptations).length > 0;

  if (!hasData) {
    return null;
  }

  return (
    <div className={Styles.crossPlatformSection}>
      <div className={Styles.sectionHeader}>
        <FiRefreshCcw className={Styles.sectionIcon} />
        <h2>Cross-Platform Insights</h2>
      </div>

      <div className={Styles.crossPlatformGrid}>
        {/* Voice Consistency Summary */}
        {consistency_summary && (
          <div className={Styles.crossCard + ' ' + Styles.fullWidth}>
            <h3>Voice Consistency</h3>
            <p className={Styles.consistencySummary}>{consistency_summary}</p>
          </div>
        )}

        {/* Shared Voice Elements */}
        {shared_voice_elements.length > 0 && (
          <div className={Styles.crossCard}>
            <h3>Shared Across All Platforms</h3>
            <ul className={Styles.sharedList}>
              {shared_voice_elements.map((element, idx) => (
                <li key={idx}>
                  <span className={Styles.sharedBullet}>✓</span>
                  {element}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Platform Adaptations */}
        {Object.keys(platform_adaptations).length > 0 && (
          <div className={Styles.crossCard}>
            <h3>How You Adapt Per Platform</h3>
            <div className={Styles.adaptationsList}>
              {Object.entries(platform_adaptations).map(([platform, adaptation]) => {
                const Icon = PLATFORM_ICONS[platform] || FiRefreshCcw;
                return (
                  <div key={platform} className={Styles.adaptationItem}>
                    <Icon
                      className={Styles.adaptationIcon}
                      style={{ color: PLATFORM_COLORS[platform] }}
                    />
                    <div className={Styles.adaptationContent}>
                      <span className={Styles.adaptationPlatform}>
                        {PLATFORM_NAMES[platform] || platform}:
                      </span>
                      <span className={Styles.adaptationText}>{adaptation}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Unique Per Platform */}
        {Object.keys(unique_per_platform).length > 0 && (
          <div className={Styles.crossCard}>
            <h3>Unique Strengths Per Platform</h3>
            <div className={Styles.uniqueList}>
              {Object.entries(unique_per_platform).map(([platform, traits]) => {
                const Icon = PLATFORM_ICONS[platform] || FiRefreshCcw;
                return (
                  <div key={platform} className={Styles.uniqueItem}>
                    <div className={Styles.uniqueHeader}>
                      <Icon style={{ color: PLATFORM_COLORS[platform], width: 16, height: 16 }} />
                      <span>{PLATFORM_NAMES[platform] || platform}</span>
                    </div>
                    <div className={Styles.uniqueTraits}>
                      {(Array.isArray(traits) ? traits : [traits]).slice(0, 3).map((trait, idx) => (
                        <span key={idx} className={Styles.uniqueTrait}>{trait}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Strengths Per Platform */}
        {Object.keys(strengths_per_platform).length > 0 && (
          <div className={Styles.crossCard}>
            <h3>What You Excel At</h3>
            <div className={Styles.strengthsList}>
              {Object.entries(strengths_per_platform).map(([platform, strength]) => {
                const Icon = PLATFORM_ICONS[platform] || FiRefreshCcw;
                return (
                  <div key={platform} className={Styles.strengthItem}>
                    <Icon
                      className={Styles.strengthIcon}
                      style={{ color: PLATFORM_COLORS[platform] }}
                    />
                    <span className={Styles.strengthPlatform}>
                      {PLATFORM_NAMES[platform] || platform}:
                    </span>
                    <span className={Styles.strengthText}>{strength}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Content Translation Opportunities */}
        {content_translation_opportunities.length > 0 && (
          <div className={Styles.crossCard + ' ' + Styles.fullWidth}>
            <h3>Content Repurposing Opportunities</h3>
            <div className={Styles.translationList}>
              {content_translation_opportunities.map((opp, idx) => (
                <div key={idx} className={Styles.translationItem}>
                  <FiArrowRight className={Styles.translationArrow} />
                  <span>{opp}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossPlatformInsights;
